/* eslint-disable react-hooks/exhaustive-deps */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from 'react';
// React 19 experimental hook
import { useEffectEvent } from 'react';
import {
  ATLAS,
  HEX_VERTICES,
  INNER_GRID_COLS,
  INNER_GRID_ROWS,
  INNER_GRID_STEP,
  REGION_LABELS,
  RDZ_INSET,
  TILE_H,
  TILE_W,
  WORLD_BOUNDS,
  tileAssetFor,
  type MapBackgroundKind,
} from '../lib/foxhole-maps.js';

const TILE_HALF_DIAG = Math.hypot(TILE_W, TILE_H) / 2;

function findNearestTileKey(wx: number, wy: number): string | null {
  let bestKey: string | null = null;
  let bestD = Infinity;
  for (const t of ATLAS) {
    const d = Math.hypot(t.x - wx, t.y - wy);
    if (d < bestD) {
      bestD = d;
      bestKey = t.key;
    }
  }
  return bestD < TILE_HALF_DIAG ? bestKey : null;
}

export interface AtlasView {
  cx: number;
  cy: number;
  scale: number;
}

export interface FoxholeAtlasHandle {
  clientToWorld(clientX: number, clientY: number): { x: number; y: number };
  worldToClient(worldX: number, worldY: number): { x: number; y: number };
  getView(): AtlasView;
  centerOn(x: number, y: number): void;
  setView(view: AtlasView): void;
}

export type AtlasOverlayRender = (
  view: AtlasView,
  markerDrag?: { id: string; worldX: number; worldY: number } | null,
  resizeDrag?: { id: string; scale: number } | null,
) => ReactNode;

export interface FoxholeAtlasProps {
  width: number;
  height: number;
  overlay?: ReactNode | AtlasOverlayRender;
  cursor?: CSSProperties['cursor'];
  showInnerGrid?: boolean;
  onViewChange?: (view: AtlasView) => void;
  /**
   * Fired on every "clean" click (pointer-down + pointer-up without drag).
   * Because `FoxholeAtlas` uses `setPointerCapture`, native `click` events are
   * retargeted to the SVG and never reach child elements. Use this callback
   * instead of attaching `onClick` handlers to SVG overlay children.
   */
  onMapClick?: (worldX: number, worldY: number, clientX: number, clientY: number) => void;
  /**
   * Return the id of the marker at the given world coords (scale provided so the
   * hit radius can be computed), or null if no marker is close enough.
   */
  onMarkerHitTest?: (worldX: number, worldY: number, scale: number) => string | null;
  /** Fired when the user clicks a marker without dragging it. */
  onMarkerClick?: (id: string, clientX: number, clientY: number) => void;
  /** Fired when the user drags a marker to a new world position. */
  onMarkerMoved?: (id: string, worldX: number, worldY: number) => void;
  /**
   * Optional constraint applied to the dragged marker's world position before
   * it is shown as a live preview and before `onMarkerMoved` is fired.
   * Use this to prevent the dragged icon from overlapping other icons.
   * Receives the raw cursor world coords and the current view scale; returns
   * the adjusted position to use. When omitted, no constraint is applied.
   */
  resolveMarkerDragPosition?: (
    id: string,
    worldX: number,
    worldY: number,
    viewScale: number,
  ) => { x: number; y: number };
  /**
   * Return info about the marker whose resize handle is at the given world coords,
   * or null if no handle is close enough.
   */
  onResizeHandleHitTest?: (worldX: number, worldY: number, scale: number) => { id: string; originX: number; originY: number; startScale: number } | null;
  /** Fired when the resize drag ends with the new scale value (0.5–1.5). */
  onMarkerResized?: (id: string, newScale: number) => void;
  initialView?: AtlasView;
  mapBackground?: MapBackgroundKind;
  /**
   * WarAPI region text items (city/POI names) projected in world coordinates.
   * Rendered with a smaller font than `REGION_LABELS`, only visible at higher zoom levels.
   */
  warLabels?: ReadonlyArray<{ wx: number; wy: number; text: string; kind: 'major' | 'minor' }>;
  /** Region keys (ATLAS tile.key) that admins have disabled. Renders a gray
   *  overlay and suppresses the inner grid / hover fill on those tiles. */
  disabledRegionKeys?: ReadonlySet<string>;
}

const MIN_SCALE = 0.05;
const MAX_SCALE = 6;
const CLICK_THRESHOLD_PX = 4;

function computeInitialView(width: number, height: number): AtlasView {
  const ww = WORLD_BOUNDS.maxX - WORLD_BOUNDS.minX;
  const wh = WORLD_BOUNDS.maxY - WORLD_BOUNDS.minY;
  const fit = Math.min(width / ww, height / wh) || 0.1;
  return { cx: 0, cy: 0, scale: fit };
}

export const FoxholeAtlas = forwardRef<FoxholeAtlasHandle, FoxholeAtlasProps>(
  function FoxholeAtlas(
    {
      width,
      height,
      overlay,
      cursor,
      showInnerGrid = true,
      onViewChange,
      onMapClick,
      onMarkerHitTest,
      onMarkerClick,
      onMarkerMoved,
      resolveMarkerDragPosition,
      onResizeHandleHitTest,
      onMarkerResized,
      initialView,
      mapBackground = 'default',
      warLabels,
      disabledRegionKeys,
    },
    handleRef,
  ): React.JSX.Element {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [view, setView] = useState<AtlasView>(
      () => initialView ?? computeInitialView(width, height),
    );
    const [hoveredTileKey, setHoveredTileKey] = useState<string | null>(null);
    const [hoveredCell, setHoveredCell] = useState<{ ci: number; cj: number } | null>(null);

    const dragRef = useRef<{
      pointerId: number;
      startClientX: number;
      startClientY: number;
      startCx: number;
      startCy: number;
      moved: boolean;
    } | null>(null);

    const activePointers = useRef<Map<number, { clientX: number; clientY: number }>>(new Map());
    const pinchRef = useRef<{ dist: number; scale: number; cx: number; cy: number } | null>(null);
    const markerDragRef = useRef<{
      id: string;
      pointerId: number;
      startClientX: number;
      startClientY: number;
      moved: boolean;
    } | null>(null);
    const [markerDragPos, setMarkerDragPos] = useState<{
      id: string;
      worldX: number;
      worldY: number;
    } | null>(null);
    const resizeDragRef = useRef<{
      id: string;
      pointerId: number;
      /** World position of the marker centre at drag start. */
      originX: number;
      originY: number;
      /** Scale stored on the marker at drag start. */
      startScale: number;
      /** Distance (world px) from pointer to origin at drag start, used to compute new scale. */
      startDist: number;
      moved: boolean;
    } | null>(null);
    const [resizeDragState, setResizeDragState] = useState<{
      id: string;
      scale: number;
    } | null>(null);
    const latestViewRef = useRef<AtlasView>(view);
    latestViewRef.current = view;

    const lastTapRef = useRef<{ clientX: number; clientY: number; time: number } | null>(null);
    const DOUBLE_TAP_MS = 300;
    const DOUBLE_TAP_PX = 40;
    const DOUBLE_TAP_ZOOM = 2;

    const initRef = useRef(false);
    useEffect(() => {
      if (initRef.current) return;
      if (width <= 0 || height <= 0) return;
      initRef.current = true;
      if (!initialView) setView(computeInitialView(width, height));
    }, [width, height, initialView]);

    const fireViewChange = useEffectEvent((v: AtlasView) => onViewChange?.(v));
    useEffect(() => {
      fireViewChange(view);
    }, [view]);

    function clientToWorld(clientX: number, clientY: number): { x: number; y: number } {
      const svg = svgRef.current;
      if (!svg) return { x: view.cx, y: view.cy };
      const rect = svg.getBoundingClientRect();
      const sx = clientX - rect.left;
      const sy = clientY - rect.top;
      const viewW = width / view.scale;
      const viewH = height / view.scale;
      return {
        x: view.cx - viewW / 2 + (sx / Math.max(rect.width, 1)) * viewW,
        y: view.cy - viewH / 2 + (sy / Math.max(rect.height, 1)) * viewH,
      };
    }

    function worldToClient(worldX: number, worldY: number): { x: number; y: number } {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const rect = svg.getBoundingClientRect();
      const viewW = width / view.scale;
      const viewH = height / view.scale;
      return {
        x: rect.left + ((worldX - (view.cx - viewW / 2)) / viewW) * rect.width,
        y: rect.top + ((worldY - (view.cy - viewH / 2)) / viewH) * rect.height,
      };
    }

    useImperativeHandle(
      handleRef,
      () => ({
        clientToWorld,
        worldToClient,
        getView: () => view,
        centerOn: (x, y) => setView((v) => ({ ...v, cx: x, cy: y })),
        setView: (next) => setView(next),
      }),
      [view, width, height],
    );

    function onPointerDown(e: PointerEvent<SVGSVGElement>): void {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      activePointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (activePointers.current.size >= 2) {
        dragRef.current = null;
        const [p0, p1] = Array.from(activePointers.current.values()) as [
          { clientX: number; clientY: number },
          { clientX: number; clientY: number },
        ];
        const dx = p1.clientX - p0.clientX;
        const dy = p1.clientY - p0.clientY;
        const midClientX = (p0.clientX + p1.clientX) / 2;
        const midClientY = (p0.clientY + p1.clientY) / 2;
        pinchRef.current = {
          dist: Math.hypot(dx, dy),
          scale: latestViewRef.current.scale,
          cx: latestViewRef.current.cx,
          cy: latestViewRef.current.cy,
        };
        const midWorld = clientToWorld(midClientX, midClientY);
        pinchRef.current.cx = midWorld.x;
        pinchRef.current.cy = midWorld.y;
      } else {
        // Check resize-handle first (higher priority than body drag).
        if (onResizeHandleHitTest) {
          const w = clientToWorld(e.clientX, e.clientY);
          const hit = onResizeHandleHitTest(w.x, w.y, view.scale);
          if (hit) {
            const startDist = Math.hypot(w.x - hit.originX, w.y - hit.originY) || 1;
            resizeDragRef.current = {
              id: hit.id,
              pointerId: e.pointerId,
              originX: hit.originX,
              originY: hit.originY,
              startScale: hit.startScale,
              startDist,
              moved: false,
            };
            dragRef.current = null;
            markerDragRef.current = null;
            return;
          }
        }
        // Check body drag.
        if (onMarkerHitTest) {
          const w = clientToWorld(e.clientX, e.clientY);
          const hitId = onMarkerHitTest(w.x, w.y, view.scale);
          if (hitId) {
            markerDragRef.current = {
              id: hitId,
              pointerId: e.pointerId,
              startClientX: e.clientX,
              startClientY: e.clientY,
              moved: false,
            };
            dragRef.current = null;
            return;
          }
        }
        dragRef.current = {
          pointerId: e.pointerId,
          startClientX: e.clientX,
          startClientY: e.clientY,
          startCx: view.cx,
          startCy: view.cy,
          moved: false,
        };
      }
    }

    function onPointerMove(e: PointerEvent<SVGSVGElement>): void {
      if (activePointers.current.has(e.pointerId)) {
        activePointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
      }
      if (e.pointerType !== 'touch') {
        const w = clientToWorld(e.clientX, e.clientY);
        const newKey = findNearestTileKey(w.x, w.y);
        if (newKey !== hoveredTileKey) setHoveredTileKey(newKey);
        if (newKey) {
          const ht = ATLAS.find((t) => t.key === newKey)!;
          const ci = Math.max(
            0,
            Math.min(
              INNER_GRID_COLS - 1,
              Math.floor((w.x - (ht.x - TILE_W / 2)) / INNER_GRID_STEP),
            ),
          );
          const cj = Math.max(
            0,
            Math.min(
              INNER_GRID_ROWS - 1,
              Math.floor((w.y - (ht.y - TILE_H / 2)) / INNER_GRID_STEP),
            ),
          );
          if (ci !== hoveredCell?.ci || cj !== hoveredCell?.cj) setHoveredCell({ ci, cj });
        } else if (hoveredCell !== null) {
          setHoveredCell(null);
        }
      }
      // Handle resize drag move.
      const rdrag = resizeDragRef.current;
      if (rdrag && rdrag.pointerId === e.pointerId) {
        const w = clientToWorld(e.clientX, e.clientY);
        const curDist = Math.hypot(w.x - rdrag.originX, w.y - rdrag.originY);
        const MIN_SCALE_V = 1.0;
        const MAX_SCALE_V = 2.0;
        const newScale = Math.min(
          MAX_SCALE_V,
          Math.max(MIN_SCALE_V, (curDist / rdrag.startDist) * rdrag.startScale),
        );
        if (!rdrag.moved) rdrag.moved = true;
        setResizeDragState({ id: rdrag.id, scale: newScale });
        return;
      }

      // Handle marker drag move.
      const mdrag = markerDragRef.current;
      if (mdrag && mdrag.pointerId === e.pointerId) {
        const dx = e.clientX - mdrag.startClientX;
        const dy = e.clientY - mdrag.startClientY;
        if (!mdrag.moved && Math.hypot(dx, dy) > CLICK_THRESHOLD_PX) mdrag.moved = true;
        if (mdrag.moved) {
          const w = clientToWorld(e.clientX, e.clientY);
          const adjusted = resolveMarkerDragPosition
            ? resolveMarkerDragPosition(mdrag.id, w.x, w.y, view.scale)
            : { x: w.x, y: w.y };
          setMarkerDragPos({ id: mdrag.id, worldX: adjusted.x, worldY: adjusted.y });
        }
        return; // never pan the map while dragging a marker
      }

      if (pinchRef.current && activePointers.current.size >= 2) {
        const [p0, p1] = Array.from(activePointers.current.values()) as [
          { clientX: number; clientY: number },
          { clientX: number; clientY: number },
        ];
        const dx = p1.clientX - p0.clientX;
        const dy = p1.clientY - p0.clientY;
        const newDist = Math.hypot(dx, dy);
        if (newDist < 1) return;
        const pinchStartScale = pinchRef.current.scale;
        const ratio = newDist / pinchRef.current.dist;
        const midClientX = (p0.clientX + p1.clientX) / 2;
        const midClientY = (p0.clientY + p1.clientY) / 2;
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const rx = (midClientX - rect.left) / Math.max(rect.width, 1);
        const ry = (midClientY - rect.top) / Math.max(rect.height, 1);
        setView((v) => {
          const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchStartScale * ratio));
          const oldViewW = width / v.scale;
          const oldViewH = height / v.scale;
          const worldX = v.cx - oldViewW / 2 + rx * oldViewW;
          const worldY = v.cy - oldViewH / 2 + ry * oldViewH;
          const newViewW = width / newScale;
          const newViewH = height / newScale;
          return {
            scale: newScale,
            cx: worldX - (rx - 0.5) * newViewW,
            cy: worldY - (ry - 0.5) * newViewH,
          };
        });
        return;
      }
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const dxScreen = e.clientX - drag.startClientX;
      const dyScreen = e.clientY - drag.startClientY;
      if (!drag.moved && Math.hypot(dxScreen, dyScreen) > CLICK_THRESHOLD_PX) drag.moved = true;
      if (!drag.moved) return;
      const dxWorld = dxScreen / view.scale;
      const dyWorld = dyScreen / view.scale;
      setView((v) => ({ ...v, cx: drag.startCx - dxWorld, cy: drag.startCy - dyWorld }));
    }

    function endDrag(e: PointerEvent<SVGSVGElement>): void {
      activePointers.current.delete(e.pointerId);
      // Save refs BEFORE the size===0 block can null them.
      const savedDrag = dragRef.current;
      const savedMarkerDrag = markerDragRef.current;
      const savedResizeDrag = resizeDragRef.current;
      if (activePointers.current.size === 0) {
        pinchRef.current = null;
        dragRef.current = null;
        markerDragRef.current = null;
        resizeDragRef.current = null;
      }
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId))
          e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      // Resize drag end.
      if (savedResizeDrag && savedResizeDrag.pointerId === e.pointerId) {
        setResizeDragState(null);
        if (savedResizeDrag.moved) {
          const w = clientToWorld(e.clientX, e.clientY);
          const curDist = Math.hypot(w.x - savedResizeDrag.originX, w.y - savedResizeDrag.originY);
          const MIN_SCALE_V = 1.0;
          const MAX_SCALE_V = 1.5;
          const finalScale = Math.min(
            MAX_SCALE_V,
            Math.max(MIN_SCALE_V, (curDist / savedResizeDrag.startDist) * savedResizeDrag.startScale),
          );
          onMarkerResized?.(savedResizeDrag.id, finalScale);
        }
        return;
      }

      // Marker drag / click end.
      if (savedMarkerDrag && savedMarkerDrag.pointerId === e.pointerId) {
        setMarkerDragPos(null);
        if (savedMarkerDrag.moved) {
          const world = clientToWorld(e.clientX, e.clientY);
          const adjusted = resolveMarkerDragPosition
            ? resolveMarkerDragPosition(savedMarkerDrag.id, world.x, world.y, view.scale)
            : { x: world.x, y: world.y };
          onMarkerMoved?.(savedMarkerDrag.id, adjusted.x, adjusted.y);
        } else {
          onMarkerClick?.(savedMarkerDrag.id, e.clientX, e.clientY);
        }
        return;
      }

      // Pinch-to-zoom end.
      if (pinchRef.current) {
        if (activePointers.current.size < 2) {
          pinchRef.current = null;
          if (activePointers.current.size === 1) {
            const remainingId = activePointers.current.keys().next().value as number;
            const remaining = activePointers.current.get(remainingId)!;
            const v = latestViewRef.current;
            dragRef.current = {
              pointerId: remainingId,
              startClientX: remaining.clientX,
              startClientY: remaining.clientY,
              startCx: v.cx,
              startCy: v.cy,
              moved: false,
            };
          }
        }
        return;
      }

      // Map pan / click end. Use savedDrag (dragRef may be null after size===0 clear).
      const drag = savedDrag;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const wasClick = !drag.moved;
      if (!wasClick) return;
      if (e.pointerType === 'touch') {
        const now = Date.now();
        const last = lastTapRef.current;
        const isDoubleTap =
          last !== null &&
          now - last.time < DOUBLE_TAP_MS &&
          Math.hypot(e.clientX - last.clientX, e.clientY - last.clientY) < DOUBLE_TAP_PX;
        if (isDoubleTap) {
          lastTapRef.current = null;
          const svg = svgRef.current;
          if (!svg) return;
          const rect = svg.getBoundingClientRect();
          const rx = (e.clientX - rect.left) / Math.max(rect.width, 1);
          const ry = (e.clientY - rect.top) / Math.max(rect.height, 1);
          setView((v) => {
            const newScale = Math.min(MAX_SCALE, v.scale * DOUBLE_TAP_ZOOM);
            const oldViewW = width / v.scale;
            const oldViewH = height / v.scale;
            const worldX = v.cx - oldViewW / 2 + rx * oldViewW;
            const worldY = v.cy - oldViewH / 2 + ry * oldViewH;
            const newViewW = width / newScale;
            const newViewH = height / newScale;
            return {
              scale: newScale,
              cx: worldX - (rx - 0.5) * newViewW,
              cy: worldY - (ry - 0.5) * newViewH,
            };
          });
          return;
        }
        lastTapRef.current = { clientX: e.clientX, clientY: e.clientY, time: now };
      }
      // Clean click on map background (no marker hit).
      if (onMapClick) {
        const world = clientToWorld(e.clientX, e.clientY);
        onMapClick(world.x, world.y, e.clientX, e.clientY);
      }
    }

    const handleWheel = useEffectEvent((e: globalThis.WheelEvent) => {
      e.preventDefault();
      if (e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const svgEl = svgRef.current;
      if (!svgEl) return;
      const rect = svgEl.getBoundingClientRect();
      const rx = (e.clientX - rect.left) / Math.max(rect.width, 1);
      const ry = (e.clientY - rect.top) / Math.max(rect.height, 1);
      setView((v) => {
        const oldViewW = width / v.scale;
        const oldViewH = height / v.scale;
        const worldX = v.cx - oldViewW / 2 + rx * oldViewW;
        const worldY = v.cy - oldViewH / 2 + ry * oldViewH;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
        const newViewW = width / newScale;
        const newViewH = height / newScale;
        return {
          scale: newScale,
          cx: worldX - (rx - 0.5) * newViewW,
          cy: worldY - (ry - 0.5) * newViewH,
        };
      });
    });

    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return;
      const listener = (e: globalThis.WheelEvent): void => handleWheel(e);
      svg.addEventListener('wheel', listener, { passive: false });
      return () => svg.removeEventListener('wheel', listener);
    }, []);

    const viewW = width / view.scale;
    const viewH = height / view.scale;
    const vbX = view.cx - viewW / 2;
    const vbY = view.cy - viewH / 2;
    const hexPoints = HEX_VERTICES.map((p) => `${p.x},${p.y}`).join(' ');

    const LABEL_FADE_START = 0.22;
    const LABEL_HIDE_SCALE = 0.42;
    const LABEL_FONT_SIZE = 110;
    const rawLabelOpacity =
      view.scale >= LABEL_HIDE_SCALE
        ? 0
        : view.scale <= LABEL_FADE_START
          ? 1
          : 1 - (view.scale - LABEL_FADE_START) / (LABEL_HIDE_SCALE - LABEL_FADE_START);
    const labelOpacity = Math.round(rawLabelOpacity * 100) / 100;

    // WarAPI labels (city / POI names) appear when the user zooms in deep
    // enough for the hex labels to feel too coarse. Kept small to avoid
    // overlapping at lower zoom levels.
    const WAR_LABEL_FADE_START = 0.9;
    const WAR_LABEL_FULL_SCALE = 1.4;
    const WAR_LABEL_MINOR_HIDE_BELOW = 1.6;
    const WAR_LABEL_FONT_MAJOR = 12;
    const WAR_LABEL_FONT_MINOR = 5;
    const rawWarLabelOpacity =
      view.scale <= WAR_LABEL_FADE_START
        ? 0
        : view.scale >= WAR_LABEL_FULL_SCALE
          ? 1
          : (view.scale - WAR_LABEL_FADE_START) / (WAR_LABEL_FULL_SCALE - WAR_LABEL_FADE_START);
    const warLabelOpacity = Math.round(rawWarLabelOpacity * 100) / 100;

    const rdzVertices = HEX_VERTICES.map((p) => {
      const len = Math.hypot(p.x, p.y) || 1;
      const k = Math.max(0, len - RDZ_INSET) / len;
      return { x: p.x * k, y: p.y * k };
    });
    const rdzPoints = rdzVertices.map((p) => `${p.x},${p.y}`).join(' ');

    const overlayNode = typeof overlay === 'function' ? overlay(view, markerDragPos, resizeDragState) : overlay;

    const hoveredTile = hoveredTileKey ? ATLAS.find((t) => t.key === hoveredTileKey) : null;
    let subgridEl: ReactNode = null;
    if (showInnerGrid && hoveredTile && hoveredCell) {
      const ox = hoveredTile.x - TILE_W / 2;
      const oy = hoveredTile.y - TILE_H / 2;
      const x0 = ox + hoveredCell.ci * INNER_GRID_STEP;
      const y0 = oy + hoveredCell.cj * INNER_GRID_STEP;
      const pas = INNER_GRID_STEP / 3;
      const lp = {
        stroke: '#4a5568',
        strokeOpacity: 0.7,
        strokeWidth: 0.5,
        vectorEffect: 'non-scaling-stroke' as const,
      };
      subgridEl = (
        <g pointerEvents="none">
          <line x1={x0} x2={x0 + INNER_GRID_STEP} y1={y0 + pas} y2={y0 + pas} {...lp} />
          <line x1={x0} x2={x0 + INNER_GRID_STEP} y1={y0 + 2 * pas} y2={y0 + 2 * pas} {...lp} />
          <line x1={x0 + pas} x2={x0 + pas} y1={y0} y2={y0 + INNER_GRID_STEP} {...lp} />
          <line x1={x0 + 2 * pas} x2={x0 + 2 * pas} y1={y0} y2={y0 + INNER_GRID_STEP} {...lp} />
        </g>
      );
    }

    return (
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`${vbX} ${vbY} ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
        className="block touch-none select-none bg-slate-950"
        style={{ cursor: cursor ?? (dragRef.current ? 'grabbing' : 'default') }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={() => {
          setHoveredTileKey(null);
          setHoveredCell(null);
        }}
      >
        <g>
          {ATLAS.map((tile) => (
            <image
              key={tile.key}
              href={tileAssetFor(tile, mapBackground)}
              x={tile.x - TILE_W / 2}
              y={tile.y - TILE_H / 2}
              width={TILE_W}
              height={TILE_H}
              preserveAspectRatio="none"
            />
          ))}
        </g>
        <g>
          {ATLAS.map((tile) => {
            const isDisabled = disabledRegionKeys?.has(tile.key) ?? false;
            const isHovered = hoveredTileKey === tile.key && !isDisabled;
            return (
            <g key={`grid-${tile.key}`} transform={`translate(${tile.x} ${tile.y})`}>
              {showInnerGrid && isHovered && (
                <g pointerEvents="none">
                  {Array.from({ length: INNER_GRID_ROWS }, (_, i) => {
                    const y = -TILE_H / 2 + i * INNER_GRID_STEP;
                    return (
                      <line
                        key={`h-${i}`}
                        x1={-TILE_W / 2}
                        x2={+TILE_W / 2}
                        y1={y}
                        y2={y}
                        stroke="#4a5568"
                        strokeOpacity={0.7}
                        strokeWidth={0.5}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                  {Array.from({ length: INNER_GRID_COLS }, (_, i) => {
                    const x = -TILE_W / 2 + i * INNER_GRID_STEP;
                    return (
                      <line
                        key={`v-${i}`}
                        x1={x}
                        x2={x}
                        y1={-TILE_H / 2}
                        y2={+TILE_H / 2}
                        stroke="#4a5568"
                        strokeOpacity={0.7}
                        strokeWidth={0.5}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                </g>
              )}
              <polygon
                points={hexPoints}
                fill={isHovered ? '#9aa3b2' : 'none'}
                fillOpacity={isHovered ? 0.07 : 0}
                stroke={isHovered ? '#c8d8f0' : '#9aa3b2'}
                strokeOpacity={isHovered ? 0.9 : 0.55}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
              <polygon
                points={rdzPoints}
                fill="none"
                stroke="#cc5050"
                strokeOpacity={0.7}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
              {isDisabled && (
                <polygon
                  points={hexPoints}
                  fill="#1a1f2a"
                  fillOpacity={0.65}
                  stroke="#2a2f3a"
                  strokeOpacity={0.9}
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              )}
            </g>
            );
          })}
        </g>
        {subgridEl}
        {overlayNode && <g>{overlayNode}</g>}
        {labelOpacity > 0 && (
          <g pointerEvents="none" opacity={labelOpacity}>
            {ATLAS.map((tile) => (
              <text
                key={`label-${tile.key}`}
                x={tile.x}
                y={tile.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={LABEL_FONT_SIZE}
                fontFamily="sans-serif"
                fontWeight="600"
                fill="white"
                stroke="#1a202c"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                style={{ paintOrder: 'stroke fill' }}
              >
                {REGION_LABELS[tile.key] ?? tile.key}
              </text>
            ))}
          </g>
        )}
        {warLabels && warLabels.length > 0 && warLabelOpacity > 0 && (
          <g pointerEvents="none" opacity={warLabelOpacity}>
            {warLabels.map((label, i) => {
              if (label.kind === 'minor' && view.scale < WAR_LABEL_MINOR_HIDE_BELOW) {
                return null;
              }
              return (
                <text
                  key={`war-label-${i}`}
                  x={label.wx}
                  y={label.wy}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={label.kind === 'major' ? WAR_LABEL_FONT_MAJOR : WAR_LABEL_FONT_MINOR}
                  fontFamily="sans-serif"
                  fontWeight={label.kind === 'major' ? 600 : 400}
                  fill="white"
                  stroke="#1a202c"
                  strokeWidth={label.kind === 'major' ? 1.2 : 0.8}
                  vectorEffect="non-scaling-stroke"
                  style={{ paintOrder: 'stroke fill' }}
                >
                  {label.text}
                </text>
              );
            })}
          </g>
        )}
      </svg>
    );
  },
);
